<template>
  <collapse :title="title">
    <validation-provider v-slot="{ validated }" rules="required">
      <contracts
        ref="contractInfo"
        v-model="model.competitionProcessContracts"
        :contractors="contractors"
        :contracts="contracts"
        :is-competition-process-from-mipe="model.isCompetitionProcessFromMipe"
        :is-read-only="isReadOnly"
        @load-contracts="onLoadContracts"
      >
        <template #contract-error>
          <error
            v-if="validated && !model.competitionProcessContracts?.length"
            message="Debe haber al menos 1 contrato agregado."
            variant="no-border"
          />
        </template>
      </contracts>
    </validation-provider>
    <div v-if="isAmendment" class="bg-white m-2 p-3 rounded">
      <strong>Partida presupuestaria(s) actuales</strong>

      <div v-if="model.isFromMipe">
        <custom-result-not-found
          v-if="pagination.data.length == 0"
          text="No cuenta con información disponible."
        />

        <budget-sources
          v-for="(item, index) in pagination.data"
          :key="index"
          :is-read-only="true"
          :item="item"
        />
        <pagination
          v-if="showPagination"
          :data="pagination"
          show-text
          @pagination-go-page="goToPage"
        />
      </div>
      <div v-else class="my-3">
        <budget-source-funds
          is-actual
          :is-read-only="isReadOnly"
          :model="model"
          :program-types="programTypes"
        />
      </div>
    </div>
    <div v-if="isAmendment" class="mb-3">
      <label>
        Vigencia del contrato actual:
      </label>
      <div class="row">
        <div class="col-12 col-md">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :custom-messages="{
              required: 'El campo es requerido.',
            }"
            rules="required"
          >
            <custom-date-picker
              v-model="model.contractDetailOriginalValidityStartDate"
              class="w-100"
              :custom-error-msg="errors[0]"
              :error="errors[0]"
              label="Fecha desde:"
              placeholder="DD/MM/YYYY"
            />
          </validation-provider>
          <div v-else-if="isReadOnly" class="my-2">
            <div>
              Fecha desde:
            </div>
            <label class="data-label">
              {{ model.contractDetailOriginalValidityStartDate | date }}
            </label>
          </div>
        </div>
        <div class="col-12 col-md">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :custom-messages="{
              required: 'El campo es requerido.',
              afterStartDate:
                'La fecha de finalización debe ser posterior a la fecha de inicio.',
            }"
            :rules="{
              required: true,
              afterStartDate: {
                start: model.contractDetailOriginalValidityStartDate,
              },
            }"
          >
            <custom-date-picker
              v-model="model.contractDetailOriginalValidityEndDate"
              class="w-100"
              :custom-error-msg="errors[0]"
              :error="errors[0]"
              label="Fecha hasta:"
              :min-date="
                model.contractDetailOriginalValidityStartDate
                  ? new Date(model.contractDetailOriginalValidityStartDate)
                  : null
              "
              placeholder="DD/MM/YYYY"
            />
          </validation-provider>
          <div v-else-if="isReadOnly">
            <div>
              Fecha hasta:
            </div>
            <label class="data-label">
              {{ model.contractDetailOriginalValidityEndDate | date }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-3">
      <label>
        Contrato original se procuró vía:
      </label>

      <div class="row mb-2">
        <div class="col-4">
          <custom-radio
            v-model="contractDetailOriginalVia"
            class="custom-radio via"
            custom-error-msg="El campo es requerido."
            name="contractDetailOriginalVia"
            :options="contractDetailOriginalViaOptions"
          />
        </div>
        <div class="col">
          <div class="row d-flex align-items-center mb-2">
            <div class="col-4 data-label text-right">
              Indicar cuantía de la cotización original
            </div>
            <div class="col">
              <validation-provider
                v-if="!isReadOnly && isViaQuotation"
                v-slot="{ errors }"
                :custom-messages="{
                  required: 'El campo es requerido.',
                  min: 'Favor de añadir un valor de cero o mayor.',
                }"
                rules="required|min_value:0"
              >
                <custom-input
                  v-model="contractDetailOriginalViaQuotation"
                  calss="w-100"
                  :custom-error-msg="errors[0]"
                  :error="errors[0]"
                  type="number"
                />
              </validation-provider>
              <label v-else-if="isReadOnly && isViaQuotation">
                {{ contractDetailOriginalViaQuotation | empty }}
              </label>
              <label v-else>
                -
              </label>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="row d-flex align-items-center mb-2">
                <div class="col-4 data-label text-right">
                  Cantidad de años autorizados para la renovación
                </div>
                <div class="col">
                  <validation-provider
                    v-if="!isReadOnly && isViaRFP"
                    v-slot="{ errors }"
                    :custom-messages="{
                      required: 'El campo es requerido.',
                      min: 'Favor de añadir un valor de cero o mayor.',
                    }"
                    rules="required|min_value:0"
                  >
                    <custom-input
                      v-model="contractDetailOriginalViaRenovationYearsQuantity"
                      calss="w-100"
                      :custom-error-msg="errors[0]"
                      :disabled="!isViaRFP"
                      :error="errors[0]"
                      type="number"
                    />
                  </validation-provider>
                  <label v-else-if="isReadOnly && isViaRFP">
                    {{
                      contractDetailOriginalViaRenovationYearsQuantity | empty
                    }}
                  </label>
                  <label v-else>
                    -
                  </label>
                </div>
              </div>
            </div>
            <div class="col-12">
              <div class="row d-flex align-items-center">
                <div class="col-4 data-label text-right">
                  Contratación original se firmó en el año
                </div>
                <div class="col">
                  <validation-provider
                    v-if="!isReadOnly && isViaRFP"
                    v-slot="{ errors }"
                    :custom-messages="{
                      required: 'El campo es requerido.',
                      min: 'Favor de añadir un valor de cero o mayor.',
                    }"
                    rules="required|min_value:0"
                  >
                    <custom-input
                      v-model="contractDetailOriginalViaSignatureYear"
                      calss="w-100"
                      :custom-error-msg="errors[0]"
                      :error="errors[0]"
                      type="number"
                    />
                  </validation-provider>
                  <label v-else-if="isReadOnly && isViaRFP">
                    {{ contractDetailOriginalViaSignatureYear | empty }}
                  </label>
                  <label v-else>
                    -
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-3">
      <label>
        Breve descripción del ámbito de los servicios incluyendo cualquier
        reporte a presentar:
      </label>
      <validation-provider
        v-if="!isReadOnly"
        v-slot="{ errors }"
        :custom-messages="{
          required: 'El campo es requerido.',
        }"
        rules="required"
      >
        <custom-text-area
          v-model="model.contractDetailServiceDescription"
          :count-classes="['d-block', 'small-counter']"
          :custom-error-msg="errors[0]"
          :error="errors[0]"
          :maxlength="3000"
          show-char-count
        />
      </validation-provider>
      <div v-else-if="isReadOnly" class="my-2 data-label">
        {{ model.contractDetailServiceDescription | empty }}
      </div>
    </div>
    <div class="mb-3">
      <label>
        Explicación y justificación de la {{ contractLabel }}. Explique por qué
        esta modificación al contrato es válida y como la misma es afín con la
        contratación original:
      </label>
      <validation-provider
        v-if="!isReadOnly"
        v-slot="{ errors }"
        :custom-messages="{
          required: 'El campo es requerido.',
        }"
        rules="required"
      >
        <custom-text-area
          v-model="model.contractDetailJustification"
          :count-classes="['d-block', 'small-counter']"
          :custom-error-msg="errors[0]"
          :error="errors[0]"
          :maxlength="3000"
          show-char-count
        />
      </validation-provider>
      <div v-else-if="isReadOnly" class="my-2 data-label">
        {{ model.contractDetailJustification | empty }}
      </div>
    </div>

    <div v-if="isAmendment" class="mb-3">
      <label>
        Cambio en la vigencia del contrato (si aplica):
      </label>
      <validation-provider
        v-slot="{ validated }"
        :custom-messages="{
          required: 'El campo es requerido.',
        }"
        rules="required"
      >
        <custom-radio
          v-model="contractDetailValidityChanged"
          class="custom-radio"
          custom-error-msg="El campo es requerido."
          :disabled="isReadOnly"
          is-inline
          name="contractDetailValidityChanged"
          :options="yesNotOptions"
        />
        <error
          v-if="
            validated &&
              ![true, false].includes(contractDetailValidityChanged?.value)
          "
          message="El campo es requerido."
          variant="no-border"
        />
      </validation-provider>
    </div>
    <div
      v-if="isRenewal || (isAmendment && model.contractDetailValidityChanged)"
    >
      <label>
        Vigencia de contrato:
      </label>
      <div class="row">
        <div class="col-12 col-md">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :custom-messages="{
              required: 'El campo es requerido.',
            }"
            rules="required"
          >
            <custom-date-picker
              v-model="model.contractDetailValidityStartDate"
              class="w-100"
              :custom-error-msg="errors[0]"
              :error="errors[0]"
              label="Fecha desde:"
              placeholder="DD/MM/YYYY"
            />
          </validation-provider>
          <div v-else-if="isReadOnly" class="my-2">
            <div>
              Fecha desde:
            </div>
            <label class="data-label">
              {{ model.contractDetailValidityStartDate | date }}
            </label>
          </div>
        </div>
        <div class="col-12 col-md">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :custom-messages="{
              required: 'El campo es requerido.',
              afterStartDate:
                'La fecha de finalización debe ser posterior a la fecha de inicio.',
            }"
            :rules="{
              required: true,
              afterStartDate: {
                start: model.contractDetailValidityStartDate,
              },
            }"
          >
            <custom-date-picker
              v-model="model.contractDetailValidityEndDate"
              class="w-100"
              :custom-error-msg="errors[0]"
              :error="errors[0]"
              label="Fecha hasta:"
              :min-date="
                model.contractDetailValidityStartDate
                  ? new Date(model.contractDetailValidityStartDate)
                  : null
              "
              placeholder="DD/MM/YYYY"
            />
          </validation-provider>
          <div v-else-if="isReadOnly">
            <div>
              Fecha hasta:
            </div>
            <label class="data-label">
              {{ model.contractDetailValidityEndDate | date }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isRenewal || isAmendment" class="my-3">
      <budget-source-funds
        :is-actual="false"
        :is-read-only="isReadOnly"
        :model="model"
        :program-types="programTypes"
      >
        <template #header>
          <strong>
            Partida presupuestaria y/o procedencia de los fondos
          </strong>
        </template>
      </budget-source-funds>
    </div>
  </collapse>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import Collapse from '@/components/common/SimpleCollapse.vue';
import CustomInput from 'CustomInput';
import CustomTextArea from 'CustomTextArea';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';
import CustomRadio from 'custom-radio';
import Contracts from './Contracts.vue';
import Error from 'custom-error';
import CONTRACT_TYPE_VIA from '@/utils/constants/purchaseServiceDeterminationCategories';
import CONTRACT_TYPE from '@/utils/constants/purchaseServiceContractType';
import RADIO_OPTIONS from '@/utils/constants/customRadioOptions';
import BudgetSourceFunds from './BudgetSourceFunds.vue';
import BudgetSources from '../BudgetSources.vue';
import { getPreviousBudgetEntries } from '@/modules/purchaseService/api.js';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';

// Constants
const DEFAULT_PAGE_SIZE = 5;

export default {
  name: 'AmendmentContractInformation',
  components: {
    Collapse,
    ValidationProvider,
    CustomInput,
    CustomTextArea,
    CustomDatePicker,
    CustomRadio,
    Contracts,
    Error,
    BudgetSourceFunds,
    BudgetSources,
    Pagination,
    CustomResultNotFound,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    programTypes: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    type: {
      type: Number,
      default: () => null,
    },
    determinationCategory: {
      type: Number,
      default: () => null,
    },
    determinationCategories: {
      type: Array,
      default: () => [],
    },
    contracts: {
      type: Object,
      default: () => ({
        data: [],
        totalItemCount: 0,
        currentPage: 1,
        count: 0,
        pageCount: 0,
      }),
    },
    contractors: { type: Array, default: () => [] },
  },
  data() {
    return {
      pagination: {
        data: [],
        pageSize: DEFAULT_PAGE_SIZE,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    hasBudgetSourceFunds() {
      return Boolean(this.model.purchaseOrderNumber?.some((x) => !x.isActual));
    },
    contractDetailOriginalViaQuotation: {
      get() {
        return this.model.contractDetailOriginalViaQuotation;
      },
      set(val) {
        this.model.contractDetailOriginalViaQuotation = val
          ? Number(val)
          : null;
      },
    },
    contractDetailOriginalViaRenovationYearsQuantity: {
      get() {
        return this.model.contractDetailOriginalViaRenovationYearsQuantity;
      },
      set(val) {
        this.model.contractDetailOriginalViaRenovationYearsQuantity = val
          ? Number(val)
          : null;
      },
    },
    contractDetailOriginalViaSignatureYear: {
      get() {
        return this.model.contractDetailOriginalViaSignatureYear;
      },
      set(val) {
        this.model.contractDetailOriginalViaSignatureYear = val
          ? Number(val)
          : null;
      },
    },
    showPagination() {
      return (
        this.pagination.data.length > 0 &&
        this.pagination.totalItemCount > this.pagination.pageSize
      );
    },
    contractDetailValidityChanged: {
      get() {
        const value = this.yesNotOptions.find(
          (x) => x.value === this.value.contractDetailValidityChanged
        );
        return value ?? {};
      },
      set(option) {
        this.$emit('input', {
          ...this.value,
          contractDetailValidityChanged: option?.value,
        });
      },
    },
    contractDetailOriginalVia: {
      get() {
        const value = this.contractDetailOriginalViaOptions.find(
          (x) => x.value === this.value.contractDetailOriginalVia
        );
        return value ?? {};
      },
      set(option) {
        this.$emit('input', {
          ...this.value,
          contractDetailOriginalVia: option?.value,
        });
      },
    },
    yesNotOptions() {
      return RADIO_OPTIONS;
    },
    title() {
      switch (this.type) {
        case CONTRACT_TYPE.Amendment:
          return 'Información del contrato a enmendar';
        case CONTRACT_TYPE.Renewal:
          return 'Información del contrato a renovar';
        default:
          return 'Información del contrato';
      }
    },
    contractLabel() {
      switch (this.type) {
        case CONTRACT_TYPE.Amendment:
          return 'enmienda ';
        case CONTRACT_TYPE.Renewal:
          return 'renovación';
        default:
          return '';
      }
    },
    isAmendment() {
      return this.type === CONTRACT_TYPE.Amendment;
    },
    isRenewal() {
      return this.type === CONTRACT_TYPE.Renewal;
    },
    contractDetailOriginalViaOptions() {
      return (this.determinationCategories ?? []).map((x) => ({
        id: x.id,
        value: x.id,
        name: x.name.toLowerCase(),
        label: x.name,
      }));
    },
    isViaQuotation() {
      return (
        CONTRACT_TYPE_VIA.Quotation === this.contractDetailOriginalVia?.value
      );
    },
    isViaRFP() {
      return CONTRACT_TYPE_VIA.RFP === this.contractDetailOriginalVia?.value;
    },
  },
  watch: {
    determinationCategory(val) {
      this.model.contractDetailOriginalVia = val;
    },
    'model.parentNumber'(val) {
      if (val && this.model.isFromMipe) {
        this.search();
      } else {
        this.pagination = {
          data: [],
          pageSize: DEFAULT_PAGE_SIZE,
          currentPage: 1,
          totalItemCount: 0,
          pageCount: 0,
          count: 0,
        };
      }
    },
  },
  async mounted() {
    if (this.model.parentNumber) {
      this.search();
    }
  },
  methods: {
    onLoadContracts(params) {
      this.$emit('load-contracts', params);
    },
    async search() {
      const { pageSize, currentPage: pageIndex } = this.pagination;
      const { data } = await getPreviousBudgetEntries({
        number: this.model.parentNumber,
        pageIndex,
        pageSize,
      });
      this.pagination = { pageSize, ...data };
    },
    async goToPage(page) {
      this.pagination.currentPage = page;
      await this.search();
    },
  },
};
</script>
<style scoped>
.custom-radio.via ::v-deep .custom-control {
  margin-bottom: 20px !important;
}
.custom-radio ::v-deep .col-12 {
  padding: 0 !important;
}
.custom-radio ::v-deep label {
  font-size: 14px !important;
  color: #727272 !important;
  padding-top: 1px;
}
::v-deep .custom-datetime-picker-container {
  width: 100% !important;
  input[type='text'] {
    width: 100%;
  }
}

::v-deep input[type='checkbox'] + label > span {
  margin-top: 0 !important;
}
::v-deep input[type='checkbox'] + label > div {
  font-size: 14px !important;
  color: #727272 !important;
}
</style>
